import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, User, Phone, Mail, FileText, Plus, Loader2, UserPlus } from 'lucide-react';
import { ClientesService, type Cliente } from '../../services/clientes/clientes.service';
import { CreateClienteDrawer } from '../clientes/CreateClienteDrawer';

export interface ClientePOS {
  id?: string;
  nombre: string;
  email?: string;
  telefono?: string;
  documento?: string;
}

interface ClienteSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (cliente: ClientePOS) => void;
  onClear: () => void;
  currentCliente?: ClientePOS | null;
}

export const ClienteSearchModal: React.FC<ClienteSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onClear,
  currentCliente,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [results, setResults] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Buscar clientes
  useEffect(() => {
    if (!debouncedTerm) {
      setResults([]);
      setSelectedIndex(-1);
      return;
    }
    setIsLoading(true);
    ClientesService.getClientes(1, 10, debouncedTerm)
      .then((res) => {
        setResults(res.data);
        setSelectedIndex(res.data.length > 0 ? 0 : -1);
      })
      .catch(() => {
        setResults([]);
        setSelectedIndex(-1);
      })
      .finally(() => setIsLoading(false));
  }, [debouncedTerm]);

  // Focus input al abrir y resetear estado
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setDebouncedTerm('');
      setResults([]);
      setSelectedIndex(-1);
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Scroll into view del item seleccionado
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback((cliente: Cliente) => {
    onSelect({
      id: cliente.id,
      nombre: `${cliente.nombres} ${cliente.apellidos}`.trim(),
      email: cliente.email,
      telefono: cliente.telefono,
      documento: cliente.numeroDocumento,
    });
    onClose();
  }, [onSelect, onClose]);

  const handleCreateSuccess = (cliente: Cliente) => {
    handleSelect(cliente);
    setIsCreateOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (results.length > 0) {
          setSelectedIndex((prev) => (prev + 1) % results.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (results.length > 0) {
          setSelectedIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        } else if (debouncedTerm && results.length === 0) {
          onClose();
          setIsCreateOpen(true);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <>
      {isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-xl shadow-2xl bg-white flex flex-col max-h-[80vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Buscar Cliente</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isLoading ? (
                  <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Buscar por nombre, cédula, email o teléfono..."
                className="w-full pl-10 pr-10 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    inputRef.current?.focus();
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
              <span className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">↑↓</span> Navegar
              <span className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 ml-1">Enter</span> Seleccionar
              <span className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 ml-1">Esc</span> Cerrar
            </p>
          </div>

          {/* Results */}
          <div ref={resultsRef} className="flex-1 overflow-y-auto px-2 py-2 min-h-[120px]">
            {debouncedTerm && !isLoading && results.length === 0 && (
              <div className="text-center py-8">
                <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">No se encontraron clientes</p>
                <button
                  onClick={() => { onClose(); setIsCreateOpen(true); }}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Crear nuevo cliente
                </button>
              </div>
            )}

            {!debouncedTerm && !currentCliente && (
              <div className="text-center py-8">
                <Search className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Escribe para buscar un cliente registrado</p>
              </div>
            )}

            {/* Current client indicator */}
            {currentCliente && !debouncedTerm && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-2">Cliente seleccionado</p>
                <button
                  onClick={() => onClear()}
                  className="w-full text-left px-3 py-3 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{currentCliente.nombre}</p>
                      {currentCliente.documento && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {currentCliente.documento}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-emerald-600 font-medium">Quitar</span>
                  </div>
                </button>
              </div>
            )}

            {/* Search results */}
            {results.map((cliente, index) => (
              <button
                key={cliente.id}
                ref={(el) => { itemRefs.current[index] = el; }}
                onClick={() => handleSelect(cliente)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full text-left px-3 py-3 rounded-lg transition-colors mb-1 group ${
                  index === selectedIndex
                    ? 'bg-emerald-50 ring-1 ring-emerald-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                    index === selectedIndex
                      ? 'bg-emerald-500'
                      : 'bg-gradient-to-r from-emerald-400 to-teal-400'
                  }`}>
                    <span className="text-white text-xs font-bold">
                      {cliente.nombres.charAt(0)}{cliente.apellidos.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {cliente.nombres} {cliente.apellidos}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {cliente.numeroDocumento && (
                        <span className="flex items-center gap-0.5">
                          <FileText className="w-3 h-3" /> {cliente.numeroDocumento}
                        </span>
                      )}
                      {cliente.telefono && (
                        <span className="flex items-center gap-0.5">
                          <Phone className="w-3 h-3" /> {cliente.telefono}
                        </span>
                      )}
                      {cliente.email && (
                        <span className="flex items-center gap-0.5 truncate">
                          <Mail className="w-3 h-3" /> {cliente.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`transition-opacity ${index === selectedIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Seleccionar</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
            <button
              onClick={() => {
                onClear();
                onClose();
              }}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Cliente ocasional
            </button>
            <button
              onClick={() => { onClose(); setIsCreateOpen(true); }}
              className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-medium text-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo cliente
            </button>
          </div>
        </div>
      </div>
      )}

      <CreateClienteDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default ClienteSearchModal;
